import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  MenuItem,
  useLoadDetailMenuItems,
} from '@rematter/modules/trade/hooks/useLoadDetailMenuItems';
import { Button, Menu } from '@rematter/ui-web';
import { CircleCheckBig, LucideMoreHorizontal } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';

// Component for the action menu cell
export type LoadActionMenuProps = {
  load: LoadRecord;
  onVoid?: (loadId: string) => void;
  onReconcile?: (load: LoadRecord) => void;
  onMarkAsShipped: (loadId: string) => void;
  onMarkAsReceived: (loadId: string) => void;
  onRefetch?: () => void;
};

export const LoadActionMenu = ({
  load,
  onVoid,
  onReconcile,
  onMarkAsShipped,
  onMarkAsReceived,
  onRefetch,
}: LoadActionMenuProps) => {
  const t = useTranslations();

  const { menuItems, popovers } = useLoadDetailMenuItems(
    {
      id: load.id,
      salesOrderId: load.salesOrderId,
      bookingId: load.bookingId,
      status: load.status,
      invoiceId: load.invoiceId,
    },
    'always',
    onVoid,
    onRefetch,
  );

  // Get next status for determining which actions to show
  const getNextStatus = React.useCallback(
    (currentStatus: LoadStatus): LoadStatus | null => {
      switch (currentStatus) {
        case enumLoadStatus.Unassigned:
          return enumLoadStatus.Pending;
        case enumLoadStatus.Pending:
          return enumLoadStatus.ContainerAtPort;
        case enumLoadStatus.ContainerAtPort:
          return enumLoadStatus.ContainerInTransit;
        case enumLoadStatus.ContainerInTransit:
          return enumLoadStatus.ContainerAtFacility;
        case enumLoadStatus.ContainerAtFacility:
          return enumLoadStatus.ContainerLoaded;
        case enumLoadStatus.ContainerLoaded:
          return enumLoadStatus.ContainerReturning;
        case enumLoadStatus.ContainerReturning:
          return enumLoadStatus.ContainerReadyToShip;
        case enumLoadStatus.ContainerReadyToShip:
          return enumLoadStatus.Shipped;
        case enumLoadStatus.Shipped:
          return enumLoadStatus.Received;
        case enumLoadStatus.Received:
          return enumLoadStatus.Reconciled;
        case enumLoadStatus.Reconciled:
        case enumLoadStatus.Voided:
          return null;
        default:
          return null;
      }
    },
    [],
  );

  const allMenuItems = React.useMemo(() => {
    const nextStatus = getNextStatus(load.status);
    const statusTransitionItems: MenuItem[] = [];

    if (nextStatus === enumLoadStatus.Shipped) {
      statusTransitionItems.push({
        label: (
          <div className="flex items-center gap-2">
            <CircleCheckBig className="size-4" />
            {t.loadStatusTransition.markAsShippedPopover.markAsShipped}
          </div>
        ),
        onAction: () => onMarkAsShipped(load.id),
      });
    }

    if (nextStatus === enumLoadStatus.Received) {
      statusTransitionItems.push({
        label: (
          <div className="flex items-center gap-2">
            <CircleCheckBig className="size-4" />
            {t.loadStatusTransition.markAsReceivedPopover.markAsReceived}
          </div>
        ),
        onAction: () => onMarkAsReceived(load.id),
      });
    }

    if (nextStatus === enumLoadStatus.Reconciled && load.reviewed) {
      statusTransitionItems.push({
        label: (
          <div className="flex items-center gap-2">
            <CircleCheckBig className="size-4" />
            {t.loadStatusTransition.reconciliation.reconcile}
          </div>
        ),
        onAction: () => onReconcile?.(load),
      });
    }
    const items = [...statusTransitionItems, ...menuItems];
    return items;
  }, [
    t,
    onReconcile,
    menuItems,
    getNextStatus,
    load,
    onMarkAsShipped,
    onMarkAsReceived,
  ]);

  return (
    <div className="@LoadActionMenu flex justify-center">
      <Menu
        trigger={
          <Button
            size="icon"
            icon={<LucideMoreHorizontal />}
            variant="outline"
          />
        }
        items={allMenuItems}
      />
      {popovers}
    </div>
  );
};
